// react
import { useEffect, useRef } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// components
import useSearchParams from '@/hooks/useStableSearchParams';
import { formatNumber } from '@/utils/formatters';

// relative
import { darkTheme, lightTheme } from '../../../components/highcharts/themes';

import { useTheme } from '@emotion/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DateTime } from 'luxon';

/**
 * ColumnChart
 * Props:
 * - data: { categories: string[], series: [{ name, data: number[] }] }
 * - title: string
 * - xLabel: string
 * - unit: string (optional)
 * - isLoading: boolean (optional)
 */
const ColumnChart = ({
  data = { categories: [], series: [] },
  title = '',
  xLabel = '',
  unit = '',
  isLoading = false,
  height = 400,
  color = '#2979ff',
  isStacked = false,
}) => {
  const chartRef = useRef(null);
  const theme = useTheme();
  const { t, i18n } = useTranslation('math');
  const chartLocale =
    i18n.resolvedLanguage === 'dk' ? 'da' : i18n.resolvedLanguage;
  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    const themeOptions =
      theme.palette?.mode === 'dark' ? darkTheme : lightTheme;

    chart.update(
      {
        chart: { backgroundColor: themeOptions.chart.backgroundColor },
        title: { style: { color: themeOptions.title.style.color } },
        xAxis: {
          lineColor: themeOptions.xAxis.lineColor,
          labels: { style: { color: themeOptions.xAxis.labels.style.color } },
          title: {
            style: themeOptions.xAxis.title?.style || {
              color: themeOptions.xAxis.title?.style?.color,
            },
          },
        },
        yAxis: [
          {
            labels: { style: { color: themeOptions.yAxis.labels.style.color } },
          },
        ],
        legend: { itemStyle: { color: themeOptions.legend.itemStyle.color } },
        tooltip: {
          backgroundColor: themeOptions.tooltip.backgroundColor,
          borderColor: themeOptions.tooltip.borderColor,
          style: { color: theme.palette.mode === 'dark' ? 'white' : 'black' },
        },
      },
      false,
    );
    chart.redraw();
  }, [theme]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;
    if (isLoading) chart.showLoading('Loading data...');
    else chart.hideLoading();
  }, [isLoading]);
  const [searchParams] = useSearchParams();
  const aggregate = searchParams.get('agg') || 'day';
  const options = {
    time: { timezone: 'Europe/Copenhagen' },
    chart: {
      type: aggregate === 'hour' ? (isStacked ? 'area' : 'line') : 'column',
    },
    title: { text: title || '' },
    xAxis: {
      type: 'datetime',
      crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },

      title: { text: xLabel || '' },
    },
    yAxis: {
      min: 0,
      crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
      title: { text: unit ? `Total [${unit}]` : undefined },
    },
    plotOptions: {
      area: {
        stacking: isStacked ? 'normal' : undefined,
      },
      column: {
        stacking: isStacked ? 'normal' : undefined,
        borderColor: 'transparent',
      },
      line: {
        marker: {
          enabled: false,
        },
      },
    },
    tooltip: {
      headerFormat: '',
      shared: isStacked,
      formatter: function () {
        const suffix = unit ? ` ${unit}` : '';
        const dateLabel = this.x
          ? DateTime.fromMillis(this.x).toLocaleString(
              aggregate === 'hour'
                ? DateTime.DATETIME_SHORT
                : DateTime.DATE_SHORT,
              { locale: chartLocale },
            )
          : this.category;

        if (isStacked && this.points?.length) {
          const rows = this.points
            .map(
              (point) =>
                `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${formatNumber(point.y)}${suffix}</b>`,
            )
            .join('<br/>');
          const total = this.points.reduce(
            (sum, point) => sum + (point.y || 0),
            0,
          );

          return `<span>${dateLabel}</span><br/><span>${t('total')}</span>: <b>${formatNumber(total)}${suffix}</b><br/><br/>${rows}<br/>`;
        }

        return `<span>${dateLabel}</span><br/><span>${this.series.name}</span>: <b>${formatNumber(this.y)}${suffix}</b>`;
      },
    },
    legend: {
      enabled: isStacked && data.series?.length > 1,
    },
    series: (data.series || []).map((s) => ({
      ...s,
      data: s.data || [],
      color: isStacked ? undefined : color,
    })),
  };

  return (
    <div style={{ width: '100%' }}>
      <HighchartsReact
        highcharts={Highcharts}
        containerProps={{ style: { height } }}
        options={options}
        ref={chartRef}
      />
    </div>
  );
};

export default ColumnChart;
